import type {
  Calendar,
  CollectionName,
  Event,
  Habit,
  HabitCheckIn,
  Reminder,
  ReminderList,
  StickyNote,
} from "@lockcal/shared";
import type { LocalRecord } from "@/data/db";

/** Create a local record ready to be persisted. */
export function newRecord<T extends Record<string, unknown>>(
  data: T & { id?: string },
  id = crypto.randomUUID(),
): LocalRecord {
  const now = Date.now();
  return {
    ...data,
    id,
    updatedAt: now,
    deletedAt: null,
    _dirty: 1,
    _deleted: 0,
  } as LocalRecord;
}

export function touch(record: LocalRecord): LocalRecord {
  return { ...record, updatedAt: Date.now(), _dirty: 1 };
}

export function softDelete(record: LocalRecord): LocalRecord {
  const now = Date.now();
  return { ...record, deletedAt: now, updatedAt: now, _dirty: 1, _deleted: 1 };
}

/* ------------------------------------------------------------------ */
/* defaults                                                           */
/* ------------------------------------------------------------------ */

export const DEFAULT_CALENDAR_COLOR = 0xff6750a4;

export function newCalendar(title: string): LocalRecord {
  return newRecord({
    title,
    colorArgb: DEFAULT_CALENDAR_COLOR,
    createdAt: Date.now(),
  });
}

export function newEvent(
  input: Partial<Event> & { calendarId: string; title: string },
): LocalRecord {
  const now = Date.now();
  const start = input.startEpochMillis ?? now;
  return newRecord({
    calendarId: input.calendarId,
    title: input.title,
    startEpochMillis: start,
    endEpochMillis: input.endEpochMillis ?? start + 3_600_000,
    isAllDay: input.isAllDay ?? false,
    rrule: input.rrule ?? null,
    repeatEndEpochMillis: input.repeatEndEpochMillis ?? null,
    alertMinutesBefore: input.alertMinutesBefore ?? null,
    customColorArgb: input.customColorArgb ?? null,
    location: input.location ?? null,
    url: input.url ?? null,
    notes: input.notes ?? null,
    createdAt: now,
    excludedOccurrenceStarts: [],
  });
}

export function newReminderList(title: string): LocalRecord {
  return newRecord({
    title,
    colorArgb: 0xff2f80ed,
    createdAt: Date.now(),
  });
}

export function newReminder(
  input: Partial<Reminder> & { listId: string; title: string },
): LocalRecord {
  const now = Date.now();
  return newRecord({
    listId: input.listId,
    title: input.title,
    notes: input.notes ?? null,
    dueEpochMillis: input.dueEpochMillis ?? null,
    isCompleted: input.isCompleted ?? false,
    completedAtEpochMillis: input.completedAtEpochMillis ?? null,
    priority: input.priority ?? 0,
    quadrant: input.quadrant ?? 0,
    alertMinutesBefore: input.alertMinutesBefore ?? null,
    rrule: input.rrule ?? null,
    repeatEveryDays: input.repeatEveryDays ?? null,
    completedOccurrenceStarts: input.completedOccurrenceStarts ?? [],
    completionHistory: input.completionHistory ?? [],
    createdAt: now,
  });
}

export function newHabit(input: Partial<Habit> & { title: string }): LocalRecord {
  const now = Date.now();
  return newRecord({
    title: input.title,
    notes: input.notes ?? null,
    icon: input.icon ?? "⭐️",
    color: input.color ?? 0xff2f80ed,
    type: input.type ?? "CHECK_IN",
    target: input.target ?? 1,
    unit: input.unit ?? null,
    healthMetric: input.healthMetric ?? null,
    startDateMillis: input.startDateMillis ?? now,
    endDateMillis: input.endDateMillis ?? null,
    repeatOption: input.repeatOption ?? "DAILY",
    recurrence: input.recurrence ?? null,
    alarms: input.alarms ?? null,
    orderIndex: input.orderIndex ?? 0,
    createdAt: now,
    activityDisplayStyle: input.activityDisplayStyle ?? "CHECK",
    activityGifTheme: input.activityGifTheme ?? "DEFAULT",
  });
}

export function newHabitCheckIn(
  habitId: string,
  dayKey: string,
  progress = 1,
): LocalRecord {
  return newRecord({
    habitId,
    dayKey,
    progress,
    createdAt: Date.now(),
  });
}

export function newStickyNote(
  input: Partial<StickyNote> & { title: string; body: string },
): LocalRecord {
  const now = Date.now();
  return newRecord({
    title: input.title,
    body: input.body,
    colorArgb: input.colorArgb ?? 0xffffd54f,
    createdAt: now,
    orderIndex: input.orderIndex ?? 0,
    pinnedDayKey: input.pinnedDayKey ?? null,
  });
}

/* ------------------------------------------------------------------ */
/* colour helpers                                                     */
/* ------------------------------------------------------------------ */

export function argbToCss(argb: number | null | undefined, fallback = "#6750a4") {
  if (argb === null || argb === undefined) return fallback;
  const a = ((argb >>> 24) & 0xff) / 255;
  const r = (argb >>> 16) & 0xff;
  const g = (argb >>> 8) & 0xff;
  const b = argb & 0xff;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

export function argbToHex(argb: number | null | undefined, fallback = "#6750a4") {
  if (argb === null || argb === undefined) return fallback;
  const r = (argb >>> 16) & 0xff;
  const g = (argb >>> 8) & 0xff;
  const b = argb & 0xff;
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
}

export const COLLECTION_LABEL: Record<CollectionName, string> = {
  calendars: "Lịch",
  events: "Sự kiện",
  reminderLists: "Danh sách",
  reminders: "Việc",
  habits: "Thói quen",
  habitCheckIns: "Đã hoàn thành",
  stickyNotes: "Ghi chú",
  settings: "Cài đặt",
};

export type { Calendar, Event, Habit, HabitCheckIn, Reminder, ReminderList, StickyNote };
